import FCMService from '@/api/FCMService'; // eslint-disable-line
import MemberService from '@/api/MemberService'; // eslint-disable-line
import router from '@/router/index'; // eslint-disable-line
import cloneDeep from 'lodash/cloneDeep';

const state = {
  selectedMember: {},
  selectedContactInformation: null,
  selectedCalendarPreferences: null,
  selectedNotificationPreferences: null,
  selectedEmployment: null,
  selectedSubscriptionPreferences: null,
  placements: null,
  selectedProfilePictureString: null,
  selectedProfilePictureIsDefault: null,
  memberCreationInformation: null,
};

function hasPushNotificationsEnabled(args) {
  function pushNotificationsEnabled() {
    return args.filter((n) => {
      if (n.isSubscribed) {
        const types = n.notificationTypeSubscriptions;
        return types.filter(t => t.isSubscribed && t.notificationTypeName === 'NOTIFICATION_TYPE.PUSH').length > 0;
      }

      return false;
    });
  }

  return (pushNotificationsEnabled().length > 0);
}

const getters = {
  getPlacementsOrderedByDate: () => (placements) => {
    const pla = cloneDeep(placements);
    return pla.sort((a, b) => new Date(b.date) - new Date(a.date));
  },
};

const actions = {
  currentMember({ commit }) {
    return MemberService.getCurrentMember().then((response) => {
      commit('selectedMember', response.data);
    });
  },
  currentMemberContactInformation({ commit }) {
    return MemberService.getCurrentMemberContactInformation().then((response) => {
      commit('selectedContactInformation', response.data);
    });
  },
  saveContactInformation({ commit }, args) {
    return MemberService.saveContactInformation(args).then(() => {
      commit('selectedContactInformation', args);
    });
  },

  currentProfilePicture({ state, getters, commit }) { // eslint-disable-line
    if (!state.selectedProfilePictureString) {
      return MemberService.getCurrentProfilePicture().then((response) => {
        commit('selectedProfilePicture', response.data);
      });
    }
    return null;
  },

  saveProfilePicture({ commit }, args) {
    commit('selectedProfilePicture', null);
    return MemberService.saveProfilePicture(args);
  },

  deleteProfilePicture({ commit }) {
    commit('selectedProfilePicture', null);
    return MemberService.deleteProfilePicture();
  },

  //* ***PREFERENCES***
  currentMemberCalendarPreferences({ commit }) {
    return MemberService.getCurrentMemberCalendarPreferences().then((response) => {
      commit('selectedCalendarPreferences', response.data);
    });
  },
  saveCalendarPreferences({ commit }, args) {
    return MemberService.saveCalendarPreferences(args).then(() => {
      commit('selectedCalendarPreferences', args);
    });
  },
  currentMemberNotificationPreferences({ commit }) {
    return MemberService.getCurrentMemberNotificationPreferences().then(
      (response) => {
        commit('selectedNotificationPreferences', response.data);
      },
    );
  },
  saveNotificationPreferences({ commit }, args) {
    return MemberService.saveNotificationPreferences(args).then(() => {
      commit('selectedNotificationPreferences', args);
      if (hasPushNotificationsEnabled(args)) {
        FCMService.requestPermission(false);
      }
    });
  },
  currentMemberEmployment({ commit }) {
    return MemberService.getCurrentMemberEmployment().then((response) => {
      commit('selectedEmployment', response.data);
    });
  },
  saveEmployment({ commit }, args) {
    return MemberService.saveEmployment(args).then(() => {
      commit('selectedEmployment', args);
    });
  },
  currentMemberSubscriptionPreferences({ commit }) {
    return MemberService.getCurrentMemberSubscriptionPreferences().then(
      (response) => {
        commit('selectedSubscriptionPreferences', response.data);
      },
    );
  },
  saveSubscriptionPreferences({ commit }, args) {
    return MemberService.saveSubscriptionPreferences(args).then(() => {
      commit('selectedSubscriptionPreferences', args);
    });
  },
  changePassword({ }, args) { // eslint-disable-line
    return MemberService.changePassword(args);
  },
  //* ******

  changeLanguage({ commit }, lang) {
    return MemberService.changeLanguage(lang).then(() => {
      commit('changeLanguage', lang);
    });
  },
  currentMemberPlacements({ commit }) {
    return MemberService.getCurrentMemberPlacements().then((response) => {
      commit('placements', response.data);
    });
  },
  deletePlacement({ commit }, args) {
    args.placement.isDeleted = true; // eslint-disable-line
    return MemberService.updateCurrentMemberPlacement(args.placement).then(() => {
      MemberService.getCurrentMemberPlacements().then((response) => {
        commit('placements', response.data);
      });
    });
  },
  authenticationValidate({ commit }, args) {
    return MemberService.authenticationValidate(args).then((response) => {
      commit('memberCreationInformation', response.data);
    });
  },
  authenticationCreate({ commit }, args) {
    return MemberService.authenticationCreate(args).then((response) => {
      if ((!response.data.error) && (args.NoPasswordEmail !== 1)) {
        commit(
          'alerts/setAlert',
          { msg: 'MEMBER_AUTH.CONFIRMATION_MESSAGE' },
          { root: true },
        );
        router.push('/');
      }
    });
  },
};

const mutations = {
  selectedMember(state, payload) {
    state.selectedMember = payload;
  },
  changeLanguage(state, payload) {
    state.selectedMember.language = payload;
  },
  selectedContactInformation(state, payload) {
    state.selectedContactInformation = payload;
  },
  selectedProfilePicture(state, payload) {
    if (payload) {
      state.selectedProfilePictureString = `data:${payload.contentType};base64,${payload.picture}`;
      state.selectedProfilePictureIsDefault = payload.isDefault;
    } else {
      state.selectedProfilePictureString = null;
      state.selectedProfilePictureIsDefault = true;
    }
  },
  selectedCalendarPreferences(state, payload) {
    state.selectedCalendarPreferences = payload;
  },
  selectedNotificationPreferences(state, payload) {
    state.selectedNotificationPreferences = payload;
  },
  selectedEmployment(state, payload) {
    state.selectedEmployment = payload;
  },
  selectedSubscriptionPreferences(state, payload) {
    state.selectedSubscriptionPreferences = payload;
  },
  placements(state, payload) {
    state.placements = payload;
  },
  memberCreationInformation(state, payload) {
    state.memberCreationInformation = payload;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
